import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiShoppingCart, FiExternalLink, FiStar, FiHeart, FiTruck } = FiIcons;

function Store() {
  const [cart, setCart] = useState([]);

  const products = [
    {
      id: 1,
      name: "Women's Supreme T-Shirt",
      price: 25,
      image: 'https://media.publit.io/file/BigCitySupreme/1a733ba2-f655-422f-9ce1-7a3bc8dc88ab.png',
      description: 'Show your Supreme pride with our women\'s fitted tee. 100% cotton, because comfort doesn\'t take breaks from rock.',
      category: 'Apparel',
      inStock: false,
      featured: true
    },
    {
      id: 2,
      name: "Women's Supreme Tank Top",
      price: 22,
      image: 'https://media.publit.io/file/BigCitySupreme/b3ae2dc3-32cf-4d0a-a089-a1fb92e30d86.png',
      description: 'Summer festival ready tank perfect for when you need maximum ventilation during intense guitar solos.',
      category: 'Apparel',
      inStock: false,
      featured: true
    },
    {
      id: 3,
      name: "Men's Supreme T-Shirt - Classic",
      price: 25,
      image: 'https://media.publit.io/file/BigCitySupreme/8d120f51-d913-4c6d-b4ca-1481492bdfa9.png',
      description: 'The classic men\'s tee. Same design we wear on our matching outfit days (which is basically every day).',
      category: 'Apparel',
      inStock: false,
      featured: true
    },
    {
      id: 4,
      name: "Men's Supreme T-Shirt - Alternate",
      price: 25,
      image: 'https://media.publit.io/file/BigCitySupreme/dd2e2db8-d266-4153-b7f4-dc6a3e0adca0.png',
      description: 'Alternate design for when you want to be different, but still part of the Supreme family.',
      category: 'Apparel',
      inStock: false,
      featured: true
    },
    {
      id: 5,
      name: 'Super Deluxe CD',
      price: 15,
      image: 'https://media.publit.io/file/BigCitySupreme/BigCitySupreme-Supreme-Deluxe.jpg',
      description: 'The physical manifestation of our musical genius. Includes all 9 tracks plus liner notes with questionable content.',
      category: 'Music',
      inStock: false,
      featured: false
    },
    {
      id: 6,
      name: 'Urban Dreams CD',
      price: 15,
      image: 'https://media.publit.io/file/BigCitySupreme/BigCitySupreme-UrbanDreams.png',
      description: 'Our sophomore album on glorious plastic. Ten tracks of urban exploration that sound best at maximum volume.',
      category: 'Music',
      inStock: false,
      featured: false
    },
    {
      id: 7,
      name: '2018 Tour Poster',
      price: 20,
      image: 'https://media.publit.io/file/BigCitySupreme/b753f681-fc70-41d0-8282-7cca06566c91.png',
      description: 'Commemorative poster from our legendary 2018 Urban Dreams tour. Perfect for your wall when you want to remember the good old days (of 2018).',
      category: 'Merchandise',
      inStock: false,
      featured: true
    },
    {
      id: 8,
      name: '2018 Alternate Tour Poster',
      price: 20,
      image: 'https://media.publit.io/file/BigCitySupreme/1bce49fa-8223-46d0-b351-6e6b4dd49246.png',
      description: 'The alternate design from our 2018 tour. Limited edition (which means we ran out of them quickly, obviously).',
      category: 'Merchandise',
      inStock: false,
      featured: true
    }
  ];

  const categories = ['All', 'Apparel', 'Music', 'Merchandise'];
  const [activeCategory, setActiveCategory] = useState('All');

  const filteredProducts = activeCategory === 'All'
    ? products
    : products.filter(product => product.category === activeCategory);

  const featuredProducts = products.filter(product => product.featured);

  const addToCart = (product) => {
    setCart([...cart, product]);
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            className="text-5xl md:text-7xl font-bold bebas-neune gradient-text mb-6"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            SUPREME STORE
          </motion.h1>
          <motion.p
            className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto mb-8"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Official Big City Supreme merchandise. 
            Because showing your support should be as loud as our music.
          </motion.p>
          <motion.div
            className="bg-red-900/20 border border-red-500/30 rounded-lg p-6 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <p className="text-red-300 font-semibold mb-2">
              🚨 STORE NOTICE 🚨
            </p>
            <p className="text-gray-300 text-sm">
              All items are currently SOLD OUT due to overwhelming demand. 
              We're as surprised as you are. Check back soon for restocks (or don't, we'll probably be sold out again).
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            className="text-4xl md:text-5xl font-bold bebas-neune gradient-text text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            Featured Items (All Sold Out)
          </motion.h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {featuredProducts.map((product, index) => (
              <motion.div
                key={product.id}
                className="bg-gradient-to-br from-black to-gray-800 rounded-lg overflow-hidden border border-red-600/20 hover:border-red-600/40 transition-all duration-300"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
              >
                <div className="relative">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-64 object-cover"
                  />
                  <div className="absolute top-4 right-4 bg-red-600 text-white px-2 py-1 rounded-full text-xs font-bold">
                    SOLD OUT
                  </div>
                  <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                    <span className="text-white font-bold text-lg">SOLD OUT</span>
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-2">{product.name}</h3>
                  <p className="text-gray-400 text-sm mb-4">{product.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-yellow-400">${product.price}</span>
                    <button
                      disabled
                      className="flex items-center space-x-2 px-4 py-2 rounded-full font-semibold transition-colors bg-gray-600 text-gray-400 cursor-not-allowed"
                    >
                      <SafeIcon icon={FiShoppingCart} className="h-4 w-4" />
                      <span>Sold Out</span>
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-black border-b border-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-2 rounded-full font-semibold transition-all duration-300 ${
                  activeCategory === category
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* All Products */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            layout
          >
            {filteredProducts.map((product, index) => (
              <motion.div
                key={`${activeCategory}-${product.id}`}
                className="bg-gray-900 rounded-lg overflow-hidden border border-gray-700 hover:border-gray-600 transition-all duration-300"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                whileHover={{ scale: 1.02 }}
                layout
              >
                <div className="relative">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-48 object-cover"
                  />
                  {product.featured && (
                    <div className="absolute top-2 left-2 bg-yellow-500 text-black px-2 py-1 rounded text-xs font-bold">
                      HOT
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                    <span className="text-white font-bold">SOLD OUT</span>
                  </div>
                </div>

                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-white text-sm">{product.name}</h3>
                    <button className="text-gray-400 hover:text-red-400 transition-colors">
                      <SafeIcon icon={FiHeart} className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="text-gray-400 text-xs mb-3 line-clamp-2">{product.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-yellow-400">${product.price}</span>
                    <button
                      disabled
                      className="flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-semibold transition-colors bg-gray-600 text-gray-400 cursor-not-allowed"
                    >
                      <SafeIcon icon={FiShoppingCart} className="h-3 w-3" />
                      <span>Sold Out</span>
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* External Store Links */}
      <section className="py-20 bg-gradient-to-r from-red-900/20 to-yellow-900/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h2
            className="text-4xl md:text-5xl font-bold bebas-neune text-white mb-8"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            Get Our Music
          </motion.h2>
          <motion.p
            className="text-xl text-gray-200 mb-8"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Can't wait for shipping? Get instant gratification with digital downloads and streaming.
          </motion.p>
          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <a
              href="https://bigcitysupreme.bandcamp.com/album/super-deluxe"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 px-8 py-3 rounded-full font-semibold transition-colors"
            >
              <SafeIcon icon={FiExternalLink} className="h-5 w-5" />
              <span>Bandcamp</span>
            </a>
            <a
              href="https://open.spotify.com/artist/1y4LSBAOXKcnWoCL0OgtHU"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 px-8 py-3 rounded-full font-semibold transition-colors"
            >
              <SafeIcon icon={FiExternalLink} className="h-5 w-5" />
              <span>Spotify</span>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Cart Notification */}
      {cart.length > 0 && (
        <motion.div
          className="fixed bottom-4 right-4 bg-red-600 text-white p-4 rounded-lg shadow-lg"
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex items-center space-x-2">
            <SafeIcon icon={FiShoppingCart} className="h-5 w-5" />
            <span>{cart.length} item(s) in cart</span>
          </div>
        </motion.div>
      )}
    </div>
  );
}

export default Store;